import { call, put, select, takeLatest } from 'redux-saga/effects'
import { getPcrIdSelector } from '../../home/selectors'
import { IPcr } from '../../pcr/store/pcr.types'
import { getUserSelector } from '../../user/selectors'
import { SubmitChartService } from '../services/submitChart.service'
import { postSubmitChart } from '../store/submitChart.actions'
import { SubmitChartConstants } from '../store/submitChart.constants'
import { SubmitChartRequest, SubmitChartResponse } from '../store/submitChart.types'
import Logger from '../../../services/logger.service'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import { zollBaseURL } from '../../../services/api.service'
import crypto from 'crypto-js'
import { handleApiError } from '../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import { getAuthenticationJwtSelector } from '../../login/selectors/getAuthenticationJwt.selector'
import { setAuthenticationJwt } from '../../login/store/login.actions'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import { setLockChart } from '../../completeChart/store/completeChart.actions'
import { callAutoUploadPostSubmitChart } from '../../home/saga/autoUploadPCRs.saga'

export function* doSubmitChartSaga(pcrDoc: IPcr, isManualUpload = true) {
  const authenticationJwt: string = yield select(getAuthenticationJwtSelector)
  const { userId, serviceId } = yield select(getUserSelector)
  const { prid, pcrId, createdBy } = pcrDoc
  // ----------------------------------------------------------
  // TODO:
  // Put logic in here that prevents are chart from getting
  // submitted when the status of the chart is Complete.
  // ----------------------------------------------------------

  // TODO: Have a better way to toggle this
  const debugMode = process.env.NODE_ENV === 'development'

  const submitChartRequest: SubmitChartRequest = {
    userId: +userId,
    prid: prid,
    serviceId: +serviceId,
    pcrDocumentName: pcrId,
    debugMode,
    pcrContent: JSON.stringify(pcrDoc),
  }

  // Note: If this contains the PRID in the future, then use Logger.sensitive
  const submissionLog = `User: ${userId} is submitting ${pcrId} in service: ${serviceId}`
  Logger.info(isManualUpload ? submissionLog : `${submissionLog}  created by ${createdBy}`)

  const submitChartService = new SubmitChartService(authenticationJwt as string)

  const response: SubmitChartResponse = yield call(
    [submitChartService, submitChartService.doSubmitChart],
    submitChartRequest
  )
  const { data, status } = response

  if (debugMode) {
    SubmitChartService.printOracleStatements(response)
  }

  response.success = false
  response.failure = false

  Logger.info(`SubmitChartResponse status: ${status}`)

  if (status === 200) {
    if (isManualUpload) yield put(setAuthenticationJwt(response.authenticationJwt))
    if (data?.prid) {
      response.success = true
      Logger.sensitive(`PRID: ${data.prid}`)

      // ----------------------------------------------------
      // Feature Toggle: Use a different URL for the QR Code
      // ----------------------------------------------------
      // If REACT_APP_USE_GET_PCR_URL_IN_QR_CODE=true OR
      // the Local Storage has a USE_GET_PCR_URL_IN_QR_CODE=true
      // ----------------------------------------------------
      const env_useGetPcrUrl = `${process.env.REACT_APP_USE_GET_PCR_URL_IN_QR_CODE}`
      const local_useGetPcrUrl = localStorage.getItem('USE_GET_PCR_URL_IN_QR_CODE')

      if (env_useGetPcrUrl === 'true' || local_useGetPcrUrl === 'true') {
        const token = crypto.SHA256(`${serviceId}:${data.prid}`).toString()
        data.emcUrl = `${zollBaseURL}/api/Pcr/get-pcr-special?serviceId=${serviceId}&prid=${data.prid}&token=${token}`
      }
      // ----------------------------------------------------
      // End: Feature Toggle
      // ----------------------------------------------------

      // ----------------------------------------------------------
      // TODO:
      // Put logic in here that sets the status of the chart to Completed
      // ----------------------------------------------------------
    } else {
      response.failure = true
      response.responseMessage = 'No PRID available in the response'
    }
  } else {
    response.failure = true

    if (response.error) {
      response.responseMessage = response.error.message
      SubmitChartService.printAPIError(response.error)
    }
    if (response.messages && response.messages.length) {
      const index = response.messages.findIndex((x) => x.status === response.status)
      response.responseMessage = response.messages[index].message
    } else {
      response.responseMessage =
        "There's an error uploading the chart: Please review the entered data in the chart or contact customer support for further assistance."
    }
    if (isManualUpload) {
      yield put(setLockChart(0, false))
      yield put(handleApiError(response))
    }
  }
  const subsetOfResponse = (({ data, success, failure, responseMessage }) => ({
    prid: data?.prid,
    emcUrl: data?.emcUrl,
    success,
    failure,
    responseMessage,
    chartLockIntended: data?.chartLockIntended,
    nemsisGenerationSuccessful: data?.nemsisGenerationSuccessful,
  }))(response)
  if (isManualUpload) {
    yield put(postSubmitChart(subsetOfResponse))
  } else {
    yield call(callAutoUploadPostSubmitChart, pcrDoc, subsetOfResponse)
  }
}

export function* submitChart() {
  try {
    const pcrDocumentName: string = yield select(getPcrIdSelector)
    const { authState, serviceId } = yield select(getUserSelector)

    if (!serviceId) {
      throw Error('serviceId does not exist')
    }

    if (!pcrDocumentName) {
      throw Error('pcrDocumentName does not exist')
    }

    if (!authState) {
      throw Error('authState does not exist')
    }

    const pcrDocsService = new PcrDocsService(serviceId)
    const pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrDocumentName)

    yield call(doSubmitChartSaga, pcrDoc)
  } catch (e: any) {
    yield put(setLockChart(0, false))
    const cause = e.message ? e.message : 'unknown'
    const response = SubmitChartService.createErrorResponse(
      `An error occurred during chart submission. Cause: ${cause}`
    )
    yield put(postSubmitChart(response))
    const errorResponse = {
      status: null,
      messages: [
        {
          status: null,
          display: [
            { id: 3, name: 'Alert' },
            { id: 1, name: 'Log' },
          ],
          message: `An error occurred during chart submission. Cause: ${e}`,
        },
      ],
    }
    yield put(handleApiError(errorResponse))
  }
}

export function* submitChartSaga() {
  yield takeLatest([SubmitChartConstants.SUBMIT_CHART], performWithMutexHOG, submitChart)
}
